import React, { useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import { RoundingRule } from './RoundingConfiguration';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import type { OrgUnit } from '../sharedFinancialData';
interface RoundingFormProps {
  rule: RoundingRule | null;
  onSave: (rule: Omit<RoundingRule, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}
export function RoundingForm({
  rule,
  onSave,
  onCancel
}: RoundingFormProps) {
  const [formData, setFormData] = useState<Omit<RoundingRule, 'id' | 'createdAt' | 'updatedAt'>>({
    name: rule?.name || '',
    description: rule?.description || '',
    roundingType: rule?.roundingType || 'Nearest',
    interval: rule?.interval || 0.01,
    scope: rule?.scope || 'Global',
    orgUnitId: rule?.orgUnitId,
    orgUnitName: rule?.orgUnitName,
    orgUnitPath: rule?.orgUnitPath,
    active: rule?.active !== undefined ? rule.active : true
  });
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    // If changing scope to Global, clear OrgUnit data
    if (name === 'scope' && value === 'Global') {
      setFormData({
        ...formData,
        [name]: value,
        orgUnitId: undefined,
        orgUnitName: undefined,
        orgUnitPath: undefined
      });
      setSelectedOrgUnit(null);
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    // Remove non-numeric characters except decimal point
    const sanitizedValue = value.replace(/[^\d.]/g, '');
    const numberValue = sanitizedValue === '' ? '' : parseFloat(sanitizedValue);
    if (!isNaN(numberValue as number)) {
      setFormData({
        ...formData,
        [name]: numberValue
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setSelectedOrgUnit(orgUnit);
    setFormData({
      ...formData,
      orgUnitId: orgUnitId,
      orgUnitName: orgUnit?.name,
      orgUnitPath: orgUnit?.path
    });
    // Clear error when field is edited
    if (errors.orgUnitId) {
      setErrors({
        ...errors,
        orgUnitId: ''
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.interval <= 0) {
      newErrors.interval = 'Interval must be greater than 0';
    }
    if (formData.scope === 'OrgUnit' && !formData.orgUnitId) {
      newErrors.orgUnitId = 'Organisation Unit is required when scope is OrgUnit';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9538">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9539">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9540">
          {rule ? 'Edit Rounding Rule' : 'Create Rounding Rule'}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-9541">
          {rule ? 'Update the details of an existing rounding rule' : 'Define a new rounding rule for use across the system'}
        </p>
      </div>
      <form onSubmit={handleSubmit} data-id="element-9542">
        <div className="px-4 py-5 sm:p-6" data-id="element-9543">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-9544">
            {/* Scope */}
            <div className="sm:col-span-3" data-id="element-9545">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-9546">
                Scope <span className="text-red-500" data-id="element-9547">*</span>
              </label>
              <div className="mt-1" data-id="element-9548">
                <select id="scope" name="scope" value={formData.scope} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9549">
                  <option value="Global" data-id="element-9550">Global</option>
                  <option value="OrgUnit" data-id="element-9551">Organisation Unit</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-9552">
                {formData.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
              </p>
            </div>

            {/* OrgUnit Selector - Only shown when scope is OrgUnit */}
            {formData.scope === 'OrgUnit' && <div className="sm:col-span-6" data-id="element-9553">
                <OrgUnitSelector value={formData.orgUnitId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={errors.orgUnitId} data-id="element-9554" />
              </div>}

            <div className="sm:col-span-4" data-id="element-9555">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-9556">
                Name <span className="text-red-500" data-id="element-9557">*</span>
              </label>
              <div className="mt-1" data-id="element-9558">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} placeholder="e.g., Round to Nearest Cent" data-id="element-9559" />
                {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-9560">{errors.name}</p>}
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-9561">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-9562">
                Description
              </label>
              <div className="mt-1" data-id="element-9563">
                <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Optional description of the rounding rule" data-id="element-9564" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-9565">
              <label htmlFor="roundingType" className="block text-sm font-medium text-gray-700" data-id="element-9566">
                Rounding Type <span className="text-red-500" data-id="element-9567">*</span>
              </label>
              <div className="mt-1" data-id="element-9568">
                <select id="roundingType" name="roundingType" value={formData.roundingType} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9569">
                  <option value="Nearest" data-id="element-9570">Nearest</option>
                  <option value="Up" data-id="element-9571">Up</option>
                  <option value="Down" data-id="element-9572">Down</option>
                  <option value="Swedish" data-id="element-9573">Swedish</option>
                  <option value="Truncate" data-id="element-9574">Truncate</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-9575">
                {formData.roundingType === 'Nearest' && 'Rounds to nearest interval (standard rounding)'}
                {formData.roundingType === 'Up' && 'Always rounds up to next interval'}
                {formData.roundingType === 'Down' && 'Always rounds down to previous interval'}
                {formData.roundingType === 'Swedish' && 'Special rounding for cash transactions'}
                {formData.roundingType === 'Truncate' && 'Removes decimal places without rounding'}
              </p>
            </div>

            <div className="sm:col-span-3" data-id="element-9576">
              <label htmlFor="interval" className="block text-sm font-medium text-gray-700" data-id="element-9577">
                Interval <span className="text-red-500" data-id="element-9578">*</span>
              </label>
              <div className="mt-1" data-id="element-9579">
                <input type="text" name="interval" id="interval" value={formData.interval} onChange={handleCurrencyChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.interval ? 'border-red-300' : ''}`} placeholder="0.01" pattern="^\d*(\.\d{0,2})?$" data-id="element-9580" />
                {errors.interval && <p className="mt-1 text-sm text-red-600" data-id="element-9581">{errors.interval}</p>}
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-9582">
                Common values: 0.01 (cent), 0.05 (nickel), 0.10 (dime), 1.00
                (dollar)
              </p>
            </div>

            <div className="sm:col-span-6" data-id="element-9583">
              <div className="flex items-start" data-id="element-9584">
                <div className="flex items-center h-5" data-id="element-9585">
                  <input id="active" name="active" type="checkbox" checked={formData.active} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-9586" />
                </div>
                <div className="ml-3 text-sm" data-id="element-9587">
                  <label htmlFor="active" className="font-medium text-gray-700" data-id="element-9588">
                    Active
                  </label>
                  <p className="text-gray-500" data-id="element-9589">
                    When checked, this rounding rule will be available for use
                    in the system
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-9590">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9591">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-9592" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9593">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-9594" />
            {rule ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </form>
    </div>;
}